import { useState } from "react";

import * as Api from "@/api";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import Valkyrie, { viAt } from "@sippy-platform/valkyrie";

import { Button, Dialog, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import PropTypes from "prop-types";

import changeEmailReducer from "./changeEmailReducer";

function ChangeEmail({ account }) {
  const [openChangeEmailAddress, setOpenChangeEmailAddress] = useState(false);
  const [changeEmailSuccess, setChangeEmailSuccess] = useState(false);

  // Update email
  const { value, dispatch, formState, errors, allErrors, save } = useSave({
    initialValue: { email: "" },
    mode: SaveMode.MANUAL,
    reducer: changeEmailReducer,
    validator: () => ({
      email: string()
        .required({ message: "Email address is required." })
        .email({ message: "Email address isn't a valid format." })
        .not(account.email, "The new email address must be different from the current email address"),
    }),
    onSubmit: () => Api.putEmailAddress(account, value),
    onSuccess: () => {
      setChangeEmailSuccess(true);
      dispatch({ type: "set", value: { email: "" } });
    },
  });

  const formHandler = (property, value) => {
    dispatch({ type: property, value });
  };

  function closeChangeEmail() {
    dispatch({ type: "set", value: { email: "" } });
    setOpenChangeEmailAddress(false);
    setChangeEmailSuccess(false);
  }

  return (
    <>
      <Button startIcon={<Valkyrie icon={viAt} />} onClick={() => setOpenChangeEmailAddress(true)}>
        Change email
      </Button>
      <Dialog open={openChangeEmailAddress} onClose={closeChangeEmail}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <DialogTitle sx={{ p: 0 }}>Change email address</DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {changeEmailSuccess ? (
              <DialogContentText>
                An email has been send to your new email address to confirm the change. The email address of this
                account will be updated as soon as it has been verified.
              </DialogContentText>
            ) : (
              <Stack
                sx={{
                  gap: 2,
                }}
              >
                <DialogContentText>
                  Enter the new email address. After changing the email address you'll receive a confirmation mail to
                  confirm the new address is correct.
                </DialogContentText>
                <InputText
                  required
                  id="email"
                  label="New email address"
                  onChange={formHandler}
                  value={value.email}
                  error={errors}
                />
              </Stack>
            )}
          </DialogContent>
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              containerType: "inline-size",
            }}
          >
            <Button variant={changeEmailSuccess ? "contained" : "outlined"} onClick={closeChangeEmail}>
              {changeEmailSuccess ? "Close" : "Cancel"}
            </Button>
            {!changeEmailSuccess && <SaveIndicator formState={formState} errors={allErrors} save={save} />}
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}

ChangeEmail.propTypes = {
  account: PropTypes.object,
};

export default ChangeEmail;
